import React from 'react';
import { InputGroup, MenuItem, Menu, Button, Popover } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { DatePicker } from '@blueprintjs/datetime';
import {
  ControlWrapper,
  SearchWrapper,
  FilterWrapper,
} from 'components/styled-components';
import Export from 'components/vendor-requests/export-list';
import isEqual from 'lodash/isEqual';
import uniq from 'lodash/uniq';
import shortid from 'shortid';
import hasPermission from 'utils/hasPermission';
import permissions from 'utils/permissions';
import constants from 'utils/constants';

const { REVIEW_STATES, DEPARTMENT_REVIEW_STATES } = constants;

const RenderedFiltersSplitValue = ({
  handleChangeFilter,
  active,
  filter,
  filterText,
  filterField,
  displayFilter
}) => {
  return (
    <MenuItem
      onClick={() => handleChangeFilter(filter, filterField, displayFilter)}
      text={filterText}
      key={shortid.generate()}
      active={isEqual(filter, active)}
    />
  );
};

const RenderedFilters = ({
  handleChangeFilter,
  active,
  filter,
  filterField,
  displayFilter
}) => {
  return (
    <MenuItem
      onClick={() => handleChangeFilter(filter, filterField, displayFilter)}
      text={filter}
      key={shortid.generate()}
      active={isEqual(filter, active)}
    />
  );
};

const DateMenu = ({
  handleDateFilterChange,
  field,
  dateCheck,
  parentActive,
  displayFilter,
}) => (
  <>
    <MenuItem text="Date ON" active={dateCheck === 'ON' && parentActive}>
      <DatePicker
        onChange={date =>
          handleDateFilterChange(field, 'ON', date, displayFilter)
        }
      />
    </MenuItem>
    <MenuItem text="Date BEFORE" active={dateCheck === 'BEFORE'}>
      <DatePicker
        onChange={date =>
          handleDateFilterChange(field, 'BEFORE', date, displayFilter)
        }
      />
    </MenuItem>
  </>
);

const FilterMenu = props => {
  return (
    <Menu>
      <MenuItem text="Clear Filters" onClick={props.clearFilters} />
      {hasPermission(props.appUser.permissions, [
        permissions.MODIFY_ALL_THIRD_PARTY_REQUESTS,
        permissions.MODIFY_OWNED_THIRD_PARTY_REQUESTS,
      ]) && (
        <MenuItem text="Status" active={props.activeParent === 'Status'}>
          {props.statusFilters.map((status, i) => (
            <RenderedFilters
              filter={status}
              {...props}
              filterField="requestStatus"
              displayFilter={`Status > ${status}`}
              key={shortid.generate()}
            />
          ))}
        </MenuItem>
      )}
      {hasPermission(
        props.appUser.permissions,
        permissions.VIEW_DEPARTMENTS
      ) && (
        <MenuItem
          text="Requesting Department"
          active={props.activeParent === 'Requesting Department'}
        >
          {props.departmentFilters.map(department => (
            <RenderedFilters
              filter={department}
              {...props}
              filterField="departmentsRequesting"
              displayFilter={`Requesting Department > ${department}`}
              key={shortid.generate()}
            />
          ))}
        </MenuItem>
      )}
      <MenuItem
        text="Product / Service"
        active={props.activeParent === 'Product / Service'}
      >
        {props.productServiceFilters.map(filter => (
          <RenderedFilters
            filter={filter}
            {...props}
            filterField="productServiceName"
            displayFilter={`Product / Service > ${filter}`}
            key={shortid.generate()}
          />
        ))}
      </MenuItem>
      <MenuItem
        text="Third Party"
        active={props.activeParent === 'Third Party'}
      >
        {props.thirdPartyNameFilters.map(filter => (
          <RenderedFilters
            filter={filter}
            {...props}
            filterField="vendorName"
            displayFilter={`Third Party > ${filter}`}
            key={shortid.generate()}
          />
        ))}
      </MenuItem>
      <MenuItem
        text="Assessment Create Date"
        active={props.dateType === 'createdDate'}
      >
        <DateMenu
          field="createdDate"
          handleDateFilterChange={props.handleDateFilterChange}
          dateCheck={props.dateCheck}
          parentActive={props.dateType === 'createdDate'}
          displayFilter="Create Date"
        />
      </MenuItem>
      <MenuItem
        text="Questionnaire Submitted Date"
        active={props.dateType === 'submittedDate'}
      >
        <DateMenu
          field="submittedDate"
          handleDateFilterChange={props.handleDateFilterChange}
          dateCheck={props.dateCheck}
          parentActive={props.dateType === 'submittedDate'}
          displayFilter="Submitted Date"
        />
      </MenuItem>
      {hasPermission(props.appUser.permissions, [
        permissions.VIEW_REQUEST_APPROVAL
      ]) && (
        <MenuItem
          text="Assessment Approved Date"
          active={props.dateType === 'approvalDate'}
        >
          <DateMenu
            field="approvalDate"
            handleDateFilterChange={props.handleDateFilterChange}
            dateCheck={props.dateCheck}
            parentActive={props.dateType === 'approvalDate'}
            displayFilter="Approval Date"
          />
        </MenuItem>
      )}
	  <MenuItem text="Review Status" active={props.activeParent === 'Review Status'}>
          {REVIEW_STATES.map((review_state) => (
            <RenderedFilters
              filter={review_state}
              {...props}
              filterField="ReviewStatus"
              displayFilter={`Review Status > ${review_state}`}
              key={shortid.generate()}
            />
          ))}
      </MenuItem>
	  <MenuItem text="My Department Reviews" active={props.activeParent === 'My Department Reviews'}>
          {DEPARTMENT_REVIEW_STATES.map((review_state) => (
            <RenderedFilters
              filter={review_state}
              {...props}
              filterField="DepartmentReviewStatus"
              displayFilter={`My Department Reviews > ${review_state}`}
              key={shortid.generate()}
            />
          ))}
      </MenuItem>
			<MenuItem text="Sponsor" active={props.activeParent === 'Sponsor'}>
				{hasPermission(props.appUser.permissions, [ permissions.VIEW_ALL_THIRD_PARTY_REQUESTS ]) ?
					props.sponsors.map(sponsor => 
						<RenderedFiltersSplitValue
							filter={sponsor.userID}
							filterText={`${sponsor.firstName} ${sponsor.lastName}`}
							{...props}
							filterField="sponsorID"
							displayFilter={`Sponsor > ${sponsor.firstName} ${sponsor.lastName}`}
							key={shortid.generate()}
						/>
					)
					:
					<RenderedFiltersSplitValue
							filter={props.appUser.userID}
							filterText={`${props.appUser.firstName} ${props.appUser.lastName}`}
							{...props}
							filterField="sponsorID"
							displayFilter={`Sponsor > ${props.appUser.firstName} ${props.appUser.lastName}`}
							key={shortid.generate()}
						/>
				}
			</MenuItem>
    </Menu>
  );
};

export const RequestControls = ({
  query,
  handleSearch,
  filter,
  displayFilter,
  requests,
  appUser,
  ...rest
}) => {
  /* Begin Creating List of filters */
  const productServiceFilters = [];
  const thirdPartyNameFilters = [];
  const statusFilters = [];
  const departmentFilters = [];

  /*
   * The data used for the filter list
   * is from the same table as requests
   * but it selects all the data (ignoring server side pagination)
   * and doesn't change that data based on what is returned from a filtered request
   */

  //ensure loaded
  if (rest.filterDataLoaded) {
    rest.filterData.forEach(filter => {
      // direct from the requests
      productServiceFilters.push(filter.productServiceName);
      thirdPartyNameFilters.push(filter.vendorInformation.vendorName);
    });
  }
  if (rest.statusList.length) {
    // use ALL the statuses in the system
    rest.statusList.forEach(filter => {
      statusFilters.push(filter.statusName);
    });
  }
  if (rest.departmentList.length) {
    // use ALL the departments in the system
    rest.departmentList.forEach(filter => {
      departmentFilters.push(filter.departmentName);
    });
  }

  return (
    <React.Fragment>
      <SearchWrapper>
        <ControlWrapper>
          <InputGroup
            leftIcon={IconNames.SEARCH}
            placeholder={`Search Assessments`}
            onChange={handleSearch}
            value={query}
            className="medium-text-group light-blue"
          />
          <div style={{ marginRight: '1rem' }} />
          <Popover
            content={
              <FilterMenu
                handleClick={rest.handleChangeFilter}
                active={filter}
                activeParent={displayFilter}
                productServiceFilters={uniq(productServiceFilters)}
                thirdPartyNameFilters={uniq(thirdPartyNameFilters)}
                statusFilters={statusFilters}
                departmentFilters={departmentFilters}
                appUser={appUser}
                {...rest}
              />
            }
            autoFocus={false}
          >
            <FilterWrapper>
              <Button
                icon={IconNames.FILTER_LIST}
                rightIcon="caret-down"
                text={displayFilter ? displayFilter : 'Filter'}
                className={`${
                  displayFilter === '' ? 'light-blue' : 'filter-selected'
                }`}
                fill={true}
                alignText="left"
              />
            </FilterWrapper>
          </Popover>
        </ControlWrapper>
        {requests.totalCount &&
          appUser.permissions.includes('EXPORT_REQUESTS') && (
            <Export exportParams={rest.exportParams} />
          )}
      </SearchWrapper>
    </React.Fragment>
  );
};
